/**
 * OfstedChip — small trust badge showing an FE college's Ofsted
 * inspection rating. Sourced from ofsted.gov.uk via the seed dataset
 * in `/app/backend/data/ofsted_ratings.py` and (in prod) the
 * `ofsted_ingest_daemon.py` CSV crawler.
 *
 * Colour semantics:
 *   outstanding → emerald (Grade 1)
 *   good        → sky (Grade 2)
 *   requires_improvement → amber (Grade 3)
 *   inadequate  → rose (Grade 4)
 *
 * Renders nothing when `rating` is null so cards without a rating
 * stay uniform-height rather than showing a placeholder.
 */
import { ShieldCheck } from "lucide-react";

const STYLES = {
  outstanding: {
    label: "Ofsted Outstanding",
    className: "border-emerald-400 bg-emerald-50 text-emerald-900",
  },
  good: {
    label: "Ofsted Good",
    className: "border-sky-400 bg-sky-50 text-sky-900",
  },
  requires_improvement: {
    label: "Ofsted Requires Improvement",
    className: "border-amber-400 bg-amber-50 text-amber-900",
  },
  inadequate: {
    label: "Ofsted Inadequate",
    className: "border-rose-400 bg-rose-50 text-rose-900",
  },
};

export default function OfstedChip({ rating, inspectionDate, className = "", size = "sm" }) {
  if (!rating) return null;
  const style = STYLES[rating];
  if (!style) return null;
  const year = inspectionDate ? String(inspectionDate).slice(0, 4) : null;
  return (
    <span
      data-testid={`ofsted-chip-${rating}`}
      className={
        `inline-flex items-center gap-1 rounded-full border ${size === "sm" ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5"} font-underground-sc tracking-wider ${style.className} ${className}`
      }
      title={inspectionDate ? `Inspected ${inspectionDate}` : undefined}
    >
      <ShieldCheck className="h-2.5 w-2.5" aria-hidden />
      {style.label}
      {year && <span className="opacity-60">· {year}</span>}
    </span>
  );
}
